import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { useToast } from '../../context/ToastContext';
import api from '../../utils/api';

const STATUS_COLORS = {
  pending: { bg: '#FEF3C7', color: '#B45309', label: '⏳ Pending' },
  resolved: { bg: '#E6F4EC', color: '#1B7F3E', label: '✅ Resolved' },
  dismissed: { bg: '#F7F8FC', color: '#64748B', label: '🚫 Dismissed' },
};

const ISSUE_LABELS = {
  incorrect_content: 'Incorrect Information',
  outdated_content: 'Outdated / Not in NCERT',
  spelling_grammar: 'Spelling / Grammar',
  wrong_example: 'Wrong Example',
  other: 'Other',
};

const AdminReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const { success, error } = useToast();

  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/content/reports');
      setReports(res.data.data || []);
    } catch { setReports([]); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/content/reports/${id}`, { status });
      setReports(prev => prev.map(r => r.id === id ? { ...r, status, resolved_at: new Date().toISOString() } : r));
      success(`Report marked as ${status}`);
    } catch { error('Failed to update report'); }
  };

  const filtered = reports.filter(r => filter === 'all' ? true : r.status === filter);

  return (
    <AdminShell title="Content Reports" subtitle="Student-reported errors and issues">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {[
            { label: 'Pending', count: reports.filter(r => r.status === 'pending').length, color: '#B45309', bg: '#FEF3C7' },
            { label: 'Resolved', count: reports.filter(r => r.status === 'resolved').length, color: '#1B7F3E', bg: '#E6F4EC' },
            { label: 'Total', count: reports.length, color: '#1A3C6E', bg: '#E8F0FB' },
          ].map(s => (
            <div key={s.label} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 900, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#64748B' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {['pending', 'resolved', 'dismissed', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, border: filter === f ? 'none' : '1px solid #E2E8F0', background: filter === f ? '#1A3C6E' : 'white', color: filter === f ? 'white' : '#64748B', cursor: 'pointer', textTransform: 'capitalize' }}>
              {f}
            </button>
          ))}
        </div>

        {/* Reports list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#64748B' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
            <div style={{ fontWeight: 700 }}>No {filter} reports</div>
          </div>
        ) : filtered.map(r => {
          const s = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
          return (
            <div key={r.id} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '2px' }}>
                    {ISSUE_LABELS[r.issue_type] || r.issue_type}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748B' }}>
                    Topic: <span style={{ fontWeight: 700, color: '#FF6B00' }}>{r.topic_id}</span> · 
                    Student: <span style={{ fontWeight: 700 }}>{r.student_name}</span> · 
                    Class {r.class_id} · 
                    {new Date(r.reported_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>
                  {s.label}
                </span>
              </div>

              <div style={{ background: '#F7F8FC', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#1A1A2E', lineHeight: 1.6, marginBottom: '12px' }}>
                "{r.description}"
              </div>

              {r.status === 'pending' && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <a href={`https://mypersonalguruji.com/study/${r.topic_id}`} target="_blank" rel="noopener noreferrer"
                    style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 700, background: '#E8F0FB', color: '#1A3C6E', textDecoration: 'none' }}>
                    👁️ View Topic
                  </a>
                  <button onClick={() => updateStatus(r.id, 'resolved')}
                    style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 700, background: '#E6F4EC', color: '#1B7F3E', border: 'none', cursor: 'pointer' }}>
                    ✅ Mark Resolved
                  </button>
                  <button onClick={() => updateStatus(r.id, 'dismissed')}
                    style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 700, background: '#F7F8FC', color: '#64748B', border: 'none', cursor: 'pointer' }}>
                    🚫 Dismiss
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AdminShell>
  );
};

export default AdminReportsPage;
